import { useMemo } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import ManualLink from "@/v2-backoffice-client/components/manual/ManualLink"
import type { ManualKey } from "@/v2-backoffice-client/components/manual/manual.constants"
import Page, { type PageProps } from "@/v2-backoffice-client/components/page"
import Tabs, { useTabParam } from "@/v2-backoffice-client/components/tabs"
import CustomerTable from "@/v2-backoffice-client/customer/CustomerTable"
import { useCustomerCountsQuery } from "@/v2-backoffice-client/customer/counts/customer.counts.query"
import CustomerCreateButton from "@/v2-backoffice-client/customer/create/CustomerCreateButton"
import {
  CUSTOMER_TABS_ID,
  CUSTOMER_VIEW_ALL,
  CUSTOMER_VIEW_CRM_MERGES,
} from "@/v2-backoffice-client/customer/customer.constants"
import CustomerMergeTable from "@/v2-backoffice-client/customer/merge/CustomerMergeTable"
import CustomerMergeTabTrigger from "@/v2-backoffice-client/customer/merge/CustomerMergeTabTrigger"

// Per view: what the board is for (behind the title's info toggle) and the
// manual that explains it (a header action while the tab is active). The
// licensing manual sits on the customers board because giving a customer
// access starts from this board's panels.
const VIEWS: Record<string, { about: string; manual: ManualKey }> = {
  [CUSTOMER_VIEW_ALL]: {
    about:
      "Every customer, with its HubSpot company, licensing and the teams and users it owns.",
    manual: "licensing",
  },
  [CUSTOMER_VIEW_CRM_MERGES]: {
    about:
      "Every company merge in HubSpot the CRM sync has seen, and what became of it here. A merge can leave two customer rows for one company: the row whose company was merged away and the row holding the survivor. The Backoffice makes them one on its own - the row with the licence or billing account keeps, with none on either side the row HubSpot kept, and the other row's teams and users move onto it - unless both rows hold money or the row to empty holds config, which a person decides. Every move is written down and can be undone with Keep separate.",
    manual: "crmMerges",
  },
}

export type CustomerPageProps = PageProps

function CustomerPage(props: CustomerPageProps) {
  const { className, ...pageProps } = props

  const [activeView] = useTabParam(CUSTOMER_TABS_ID, CUSTOMER_VIEW_ALL)
  const view = VIEWS[activeView] ?? VIEWS[CUSTOMER_VIEW_ALL]

  // Customers per governing license status (0 = no license), feeding the
  // board's status pills, plus the over-seat-cap count behind the Over
  // seats pill.
  const { data, loading, error } = useCustomerCountsQuery()
  const buckets = data?.getCustomerCounts.byLicenseStatus
  const overSeatCapCount = data?.getCustomerCounts.overSeatCap
  const statusCounts = useMemo(() => {
    if (!buckets) return undefined
    return new Map(
      buckets.map((bucket) => [bucket.LicenseStatusCode, bucket.CustomerCount]),
    )
  }, [buckets])

  errorToast(error, {
    title: "Failed to query for customer counts",
  })

  return (
    <Page className={cn("customer-page", className)} {...pageProps}>
      <Page.Header>
        <Page.Title about={view.about}>Customers</Page.Title>
        <Page.Header.Actions>
          <ManualLink manual={view.manual} />
          <CustomerCreateButton />
        </Page.Header.Actions>
      </Page.Header>

      <Page.Content className="flex-1 min-h-0 flex flex-col">
        <Tabs id={CUSTOMER_TABS_ID} defaultValue={CUSTOMER_VIEW_ALL}>
          <Tabs.List>
            <Tabs.Trigger value={CUSTOMER_VIEW_ALL} isLoading={loading}>
              All Customers
            </Tabs.Trigger>
            <CustomerMergeTabTrigger />
          </Tabs.List>

          <Tabs.Content value={CUSTOMER_VIEW_ALL} className="min-h-0">
            <CustomerTable
              isLoading={loading}
              statusCounts={statusCounts}
              overSeatCapCount={overSeatCapCount}
            />
          </Tabs.Content>

          <Tabs.Content value={CUSTOMER_VIEW_CRM_MERGES} className="min-h-0">
            <CustomerMergeTable />
          </Tabs.Content>
        </Tabs>
      </Page.Content>
    </Page>
  )
}

export default CustomerPage
